import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { useDispatch } from 'react-redux'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Link,
  Typography,
  useTheme,
} from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import WorkOutlineIcon from '@mui/icons-material/WorkOutline'
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline'
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch'
import { PLAN_OPTIONS } from 'components/UpgradePlanDialog'
import { fetchCreateCheckoutSession } from 'src/redux/employerUserSignup'
import { fetchTrackEvent, ProductAnalyticsEventTypes } from 'src/redux/productAnalytics'

const TIER_DISPLAY = [
  { key: 'excellent', label: 'Excellent', backgroundColor: '#4caf50', color: '#fff' },
  { key: 'strong', label: 'Strong', backgroundColor: '#2196f3', color: '#fff' },
  { key: 'potential', label: 'Potential', backgroundColor: '#9e9e9e', color: '#fff' },
]

const EmployerUserConfirmationStep = ({ variant = 'postJobConfirmation', wizardData, onSkipToFreeJob }) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const theme = useTheme()
  const [checkoutLoading, setCheckoutLoading] = useState(false)

  const eventName = variant === 'plans'
    ? 'signup_plans_page_viewed'
    : 'signup_post_job_confirmation_viewed'

  useEffect(() => {
    dispatch(
      fetchTrackEvent({
        eventName,
        eventType: ProductAnalyticsEventTypes.ACTION,
        distinctId: wizardData.email,
        eventData: {
          source: 'signup_wizard',
          totalMatches: wizardData.matchCounts?.total || 0,
        },
      }),
    )
  }, [dispatch, wizardData.email, wizardData.matchCounts, eventName])

  const handlePlanCtaClick = (plan) => {
    dispatch(
      fetchTrackEvent({
        eventName: 'signup_plan_option_clicked',
        eventType: ProductAnalyticsEventTypes.BUTTON_CLICK,
        distinctId: wizardData.email,
        eventData: { planName: plan.key },
      }),
    )
    if (plan.key === 'professional') {
      setCheckoutLoading(true)
      dispatch(fetchCreateCheckoutSession({ customerEmail: wizardData.email, source: 'signup_wizard' }))
    } else if (plan.key === 'hospital') {
      navigate('/demo')
    }
  }
  const matchCounts = wizardData.matchCounts || {}
  const totalMatches = matchCounts.total || 0

  const planCards = (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
        gap: 2,
      }}
    >
      {PLAN_OPTIONS.map(plan => {
        const isLoading = checkoutLoading && plan.key === 'professional'
        return (
          <Card
            key={plan.key}
            variant='outlined'
            sx={{
              borderRadius: '12px',
              borderColor: plan.isHighlighted ? theme.palette.primary.main : theme.palette.grey[200],
              borderWidth: plan.isHighlighted ? 2 : 1,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
              <Typography variant='h6' fontWeight={700} gutterBottom>
                {plan.name}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mb: 1 }}>
                <Typography variant='h5' fontWeight={800}>
                  {plan.price}
                </Typography>
                {plan.priceSuffix && (
                  <Typography variant='body2' color='text.secondary'>
                    {plan.priceSuffix}
                  </Typography>
                )}
              </Box>
              {plan.description && (
                <Typography variant='body2' color='text.secondary' sx={{ mb: 1.5 }}>
                  {plan.description}
                </Typography>
              )}
              <Box component='ul' sx={{ pl: 2, mb: 2, mt: 0, flexGrow: 1 }}>
                {plan.features.map(feature => (
                  <Typography
                    key={feature}
                    component='li'
                    variant='body2'
                    color='text.secondary'
                    sx={{ mb: 0.5 }}
                  >
                    {feature}
                  </Typography>
                ))}
              </Box>
              <Button
                variant={plan.ctaVariant}
                fullWidth
                disabled={isLoading || plan.ctaDisabled}
                startIcon={isLoading ? <CircularProgress size={16} color='inherit' /> : null}
                onClick={() => handlePlanCtaClick(plan)}
              >
                {isLoading ? 'Redirecting to checkout...' : plan.ctaLabel}
              </Button>
            </CardContent>
          </Card>
        )
      })}
    </Box>
  )

  if (variant === 'plans') {
    return (
      <Box sx={{ pt: 6, pb: 10 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant='h3' fontWeight={800} gutterBottom>
            Choose your plan
          </Typography>
          <Typography variant='body1' color='text.secondary' sx={{ maxWidth: 520, mx: 'auto' }}>
            Unlock full access to our candidate network. Post unlimited jobs, view matched profiles, and use advanced search filters.
          </Typography>
        </Box>

        {planCards}

        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant='body2' color='text.secondary'>
            Not ready to subscribe?{' '}
            <Link
              component='button'
              variant='body2'
              fontWeight={600}
              onClick={onSkipToFreeJob}
              sx={{ verticalAlign: 'baseline' }}
            >
              Post your first job for free
            </Link>
          </Typography>
        </Box>
      </Box>
    )
  }

  return (
    <Box sx={{ pt: 6, pb: 10 }}>
      <Box
        sx={{
          textAlign: 'center',
          mb: 5,
          background: `linear-gradient(135deg, ${theme.palette.success.light}15, ${theme.palette.primary.light}15)`,
          borderRadius: '16px',
          p: { xs: 4, md: 5 },
        }}
      >
        <CheckCircleOutlineIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
        <Typography variant='h3' fontWeight={800} gutterBottom>
          Congratulations!
        </Typography>
        <Typography variant='h5' color='text.secondary' sx={{ mb: 1 }}>
          Your free job posting is live on MyStethi.
        </Typography>
        <Typography variant='body1' color='text.secondary' sx={{ maxWidth: 520, mx: 'auto' }}>
          Candidates can now discover your position and express interest. Here's what to do next.
        </Typography>

        {totalMatches > 0 && (
          <Box sx={{ mt: 3 }}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant='subtitle2' color='text.secondary' sx={{ mb: 1.5 }}>
              Already {totalMatches} potential {totalMatches === 1 ? 'candidate match' : 'candidate matches'}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
              {TIER_DISPLAY.map(tier => {
                const count = matchCounts[tier.key] || 0
                if (count === 0) return null
                return (
                  <Chip
                    key={tier.key}
                    label={`${count} ${tier.label}`}
                    sx={{ backgroundColor: tier.backgroundColor, color: tier.color }}
                    size='medium'
                  />
                )
              })}
            </Box>
          </Box>
        )}
      </Box>

      <Typography variant='h6' fontWeight={700} sx={{ mb: 2 }}>
        Next Steps
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 5 }}>
        <Card variant='outlined' sx={{ borderRadius: '12px' }}>
          <CardContent sx={{ p: 3, display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <WorkOutlineIcon sx={{ color: 'primary.main', fontSize: 28, mt: 0.25 }} />
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant='subtitle1' fontWeight={600}>
                Review and manage your posting
              </Typography>
              <Typography variant='body2' color='text.secondary' sx={{ mb: 1.5 }}>
                Edit your job details, update the description, or add compensation info to attract more candidates.
              </Typography>
              <Button variant='outlined' size='small' onClick={() => navigate('/jobs')}>
                Go to My Jobs
              </Button>
            </Box>
          </CardContent>
        </Card>

        <Card variant='outlined' sx={{ borderRadius: '12px' }}>
          <CardContent sx={{ p: 3, display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <PeopleOutlineIcon sx={{ color: 'primary.main', fontSize: 28, mt: 0.25 }} />
            <Box>
              <Typography variant='subtitle1' fontWeight={600}>
                Candidates are already searching
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                Active job-seekers on the platform can find your position right now. You'll be notified when candidates express interest.
              </Typography>
            </Box>
          </CardContent>
        </Card>

        <Card variant='outlined' sx={{ borderRadius: '12px' }}>
          <CardContent sx={{ p: 3, display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <RocketLaunchIcon sx={{ color: 'primary.main', fontSize: 28, mt: 0.25 }} />
            <Box>
              <Typography variant='subtitle1' fontWeight={600}>
                Unlock full candidate access
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                Upgrade to view matched candidate profiles, post unlimited jobs, and use advanced search filters.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>

      <Divider sx={{ mb: 4 }} />

      <Typography variant='h6' fontWeight={700} sx={{ mb: 0.5 }}>
        Ready to go further?
      </Typography>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
        Your free posting is just the beginning. Upgrade to unlock everything MyStethi has to offer.
      </Typography>

      {planCards}

      <Box sx={{ textAlign: 'center', mt: 4 }}>
        <Button variant='text' onClick={() => navigate('/jobs')}>
          Go to Dashboard
        </Button>
      </Box>
    </Box>
  )
}

export default EmployerUserConfirmationStep
